import React, { createContext, useContext } from 'react';
import { createTheme } from '@mui/material/styles';
import { CHART_SERIES_COLORS } from '../utils/chartTheme';

const ThemeContext = createContext();

export function useTheme() {
  return useContext(ThemeContext);
}

const darkTheme = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#4E85EB',
      light: '#6A9BF0',
      dark: '#3A6BC7',
    },
    secondary: {
      main: '#7E58B5',
      light: '#9A79C8',
      dark: '#634091',
    },
    background: {
      // Three DISTINGUISHABLE steps in one blue-grey family: page, card, raised.
      // The old ramp stepped 5.3 then 3.5 in CIELAB lightness, so the second
      // step was effectively invisible and headers read as part of their card.
      // These step 6.9 then 6.3 — near-equal, still subtle. `paper` is
      // deliberately unchanged: every card that already looks right stays put.
      default: '#0D0F14',
      paper: '#1A1E26',
      alt: '#262B35',
    },
    text: {
      primary: '#F0F2F5',
      secondary: '#ADB5BD',
    },
    error: {
      main: '#E84A5F',
    },
    warning: {
      main: '#F0B25A',
    },
    success: {
      main: '#2EB998',
    },
    info: {
      main: '#4E85EB',
    },
    // Outlined Paper draws its edge from the divider, and with the blanket Paper
    // shadow gone (see MuiPaper below) that edge is what separates a card from
    // the surface under it.
    divider: 'rgba(255, 255, 255, 0.10)',
    custom: {
      tableHeader: '#262B35',
      // A header DARKER than its own card reads as a hole punched in the
      // surface; on a dark theme, emphasis sits one step above.
      cardHeader: '#262B35',
      highlight: 'rgba(78, 133, 235, 0.08)',
      buttonGradient: 'linear-gradient(to right, #4E85EB 0%, #4E95EB 100%)',
      chartColors: CHART_SERIES_COLORS,
    },
  },
};


const commonTheme = {
  typography: {
    fontFamily: '"Inter", "Roboto", "Arial", sans-serif',
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '0.9rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
    body1: {
      fontSize: '0.9rem',
      letterSpacing: '0.01em',
    },
    body2: {
      fontSize: '0.8rem',
      letterSpacing: '0.01em',
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 6,
  },
};

const getDarkComponents = (isDark) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: 6,
        transition: 'all 0.2s',
        padding: '8px 16px',
        fontWeight: 600,
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: isDark ? '0 4px 8px rgba(0, 0, 0, 0.3)' : '0 4px 8px rgba(0, 0, 0, 0.1)',
        },
      },
      containedPrimary: {
        background: isDark 
          ? 'linear-gradient(to right, #4E85EB 0%, #4E95EB 100%)' 
          : 'linear-gradient(to right, #3A6BC7 0%, #4E85EB 100%)',
        boxShadow: isDark 
          ? '0 2px 4px rgba(78, 133, 235, 0.2)' 
          : '0 2px 4px rgba(58, 107, 199, 0.2)',
      },
      containedSecondary: {
        background: isDark 
          ? 'linear-gradient(to right, #7E58B5 0%, #9A79C8 100%)' 
          : 'linear-gradient(to right, #634091 0%, #7E58B5 100%)',
        boxShadow: isDark 
          ? '0 2px 4px rgba(126, 88, 181, 0.2)' 
          : '0 2px 4px rgba(99, 64, 145, 0.2)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: 8,
        // No blanket shadow here. `styleOverrides.root` is applied AFTER the
        // component's own `boxShadow: shadows[elevation]`, so a value on this
        // rule silently overrode the elevation ramp for every Dialog, Menu and
        // Popover and put a heavy halo on flat outlined cards. Depth now comes
        // from the surface step plus the border below; anything that genuinely
        // floats gets it from its elevation.
        border: isDark 
          ? '1px solid rgba(255, 255, 255, 0.08)' 
          : '1px solid rgba(0, 0, 0, 0.04)',
      },
      elevation1: {
        boxShadow: isDark 
          ? '0 2px 8px rgba(0, 0, 0, 0.2)' 
          : '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: isDark 
          ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
          : '0 2px 6px rgba(0, 0, 0, 0.06)',
        backdropFilter: 'blur(8px)',
        background: isDark 
          ? 'rgba(13, 15, 20, 0.9)' 
          : '#FFFFFF',
        borderBottom: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.06)',
        borderRadius: 0,
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        background: isDark ? '#1A1E26' : '#FAFBFC',
        borderRight: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.06)',
        boxShadow: isDark 
          ? 'none' 
          : '2px 0 4px rgba(0, 0, 0, 0.03)',
        borderRadius: 0,
        width: '200px',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        border: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.05)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDark 
            ? '0 8px 16px rgba(0, 0, 0, 0.4)' 
            : '0 4px 12px rgba(0, 0, 0, 0.1)',
          borderColor: isDark 
            ? 'rgba(78, 133, 235, 0.1)' 
            : 'rgba(58, 107, 199, 0.15)',
        },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        background: isDark ? '#262B35' : '#F5F7FA',
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.06)',
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        background: isDark ? '#1A1E26' : '#FFFFFF',
        borderRadius: 8,
        boxShadow: isDark 
          ? '0 4px 12px rgba(0, 0, 0, 0.2)' 
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
        border: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.06)',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        background: isDark ? '#262B35' : '#E8EAED',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 600,
        color: isDark ? '#F0F2F5' : '#1A1E26',
        borderBottom: isDark 
          ? '2px solid rgba(255, 255, 255, 0.05)' 
          : '2px solid rgba(0, 0, 0, 0.08)',
      },
      body: {
        borderBottom: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.05)',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: isDark 
            ? 'rgba(78, 133, 235, 0.04) !important' 
            : 'rgba(58, 107, 199, 0.06) !important',
        },
        // No striping by sibling position (nth-of-type) here. A rule that depends on a
        // row's position makes the browser restyle every following row whenever a row
        // is added or removed above it, which the Data page grid does on every scroll
        // step. A table that wants stripes sets them per row from its row index, as
        // that grid does.
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        marginBottom: '1rem',
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: isDark 
              ? 'rgba(255, 255, 255, 0.08)' 
              : 'rgba(0, 0, 0, 0.12)',
          },
          '&:hover fieldset': {
            borderColor: isDark 
              ? 'rgba(78, 133, 235, 0.3)' 
              : 'rgba(58, 107, 199, 0.4)',
          },
          '&.Mui-focused fieldset': {
            borderColor: isDark ? '#4E85EB' : '#3A6BC7',
          },
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '& .MuiTabs-indicator': {
          backgroundColor: isDark ? '#4E85EB' : '#3A6BC7',
          height: 2,
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        '&.Mui-selected': {
          color: isDark ? '#6A9BF0' : '#4E85EB',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        fontWeight: 500,
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {
        padding: 8,
      },
      track: {
        backgroundColor: isDark ? '#262B35' : '#E0E0E0',
        opacity: 1,
      },
      thumb: {
        backgroundColor: isDark ? '#FFF' : '#FFF',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        backgroundImage: 'none',
        borderRadius: 12,
        border: isDark 
          ? '1px solid rgba(255, 255, 255, 0.03)' 
          : '1px solid rgba(0, 0, 0, 0.05)',
        backdropFilter: 'blur(12px)',
      },
    },
  },
});

// Decode has ONE theme: the dark one. A light mode used to be offered from
// Settings, but the product's result surfaces — chart cards, tables, dialogs —
// are designed against the dark surface, and a second theme meant every one of
// them had to be right twice. `mode` is still exposed for readers of the
// context; it is always 'dark'.
export function ThemeProvider({ children }) {
  const theme = React.useMemo(() => createTheme({
    ...commonTheme,
    ...darkTheme,
    // MUI indexes this array by elevation and it must hold exactly 25 entries.
    // It held 24, so `shadows[24]` was undefined — and Dialog renders at
    // elevation 24, which is why every dialog logged an MUI error. The brand
    // defines the first four steps; the rest repeat the deepest one.
    shadows: [
      'none',
      '0px 2px 4px rgba(0, 0, 0, 0.3)',
      '0px 4px 8px rgba(0, 0, 0, 0.4)',
      '0px 8px 16px rgba(0, 0, 0, 0.5)',
      ...Array(21).fill('0px 8px 16px rgba(0, 0, 0, 0.5)')
    ],
    components: getDarkComponents(true),
  }), []);

  const value = {
    mode: 'dark',
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}